import { FeedbackRequest } from '@/types/api';
import { submitFeedback } from '@/api/feedback';
import { toast } from '@/components/ui/use-toast';

export interface StorageDriver {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

interface StorageKeys {
  GAME_PROGRESS: string;
  MASTERED_WORDS: string;
  HIGH_SCORES: string;
  DIFFICULTY_PREFERENCE: string;
  GRADE_LEVEL_PREFERENCE: string;
  SOUND_PREFERENCE: string;
  AUTO_SUBMIT_PREFERENCE: string;
  USERNAME: string;
  FEEDBACK: string;
  WELCOME_PREFERENCE: string;
}

const STORAGE_KEYS: StorageKeys = {
  GAME_PROGRESS: 'buzzy_game_progress',
  MASTERED_WORDS: 'buzzy_mastered_words',
  HIGH_SCORES: 'buzzy_high_scores',
  DIFFICULTY_PREFERENCE: 'buzzy_difficulty_preference',
  GRADE_LEVEL_PREFERENCE: 'buzzy_grade_level_preference',
  SOUND_PREFERENCE: 'buzzy_sound_preference',
  AUTO_SUBMIT_PREFERENCE: 'buzzy_auto_submit_preference',
  USERNAME: 'buzzy_username',
  FEEDBACK: 'buzzy_feedback',
  WELCOME_PREFERENCE: 'buzzy_welcome_preference',
};

const localStorageDriver: StorageDriver = {
  getItem: (key: string) => localStorage.getItem(key),
  setItem: (key: string, value: string) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
};

let activeStorageDriver: StorageDriver = localStorageDriver;

const getStorageDriver = (): StorageDriver => activeStorageDriver;

export const setStorageDriver = (driver: StorageDriver): void => {
  if (
    !driver ||
    typeof driver.getItem !== 'function' ||
    typeof driver.setItem !== 'function' ||
    typeof driver.removeItem !== 'function'
  ) {
    throw new Error(
      'Invalid storage driver: expected getItem, setItem, and removeItem functions.'
    );
  }

  activeStorageDriver = driver;
};

export const resetStorageDriver = (): void => {
  activeStorageDriver = localStorageDriver;
};

const safeSetItem = (key: string, value: string): boolean => {
  const storageDriver = getStorageDriver();

  try {
    storageDriver.setItem(key, value);
    return true;
  } catch (error) {
    let feedbackCleared = false;
    if (
      error instanceof DOMException &&
      (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
    ) {
      try {
        const feedback = storageDriver.getItem(STORAGE_KEYS.FEEDBACK);
        if (feedback && feedback !== '[]') {
          console.warn('Quota exceeded. Clearing old feedback data to free up space.');
          storageDriver.removeItem(STORAGE_KEYS.FEEDBACK);
          feedbackCleared = true;
          try {
            storageDriver.setItem(key, value);
            return true;
          } catch (retryError) {
            console.error('Failed to save even after clearing feedback:', retryError);
          }
        }
      } catch (clearError) {
        console.error('Failed to clear old feedback:', clearError);
      }

      toast({
        title: 'Storage Full',
        description: feedbackCleared
          ? 'Your local storage is full. We cleared saved feedback to try again, but the save still failed.'
          : 'Your local storage is full. Some progress may not be saved.',
        variant: 'destructive',
      });
      return false;
    }

    throw error;
  }
};

// StorageRecord uses Record<string, unknown> for flexibility with serializable data
// while avoiding the overly permissive 'any' type. This is justified because:
// 1. Storage can contain various user data structures (progress, scores, preferences)
// 2. Data is serialized to/from JSON, so it must be serializable
// 3. Individual methods provide specific types for their domain objects
type StorageRecord = Record<string, unknown>;

const readObject = (key: string): StorageRecord => {
  const data = getStorageDriver().getItem(key);
  return JSON.parse(data || '{}') as StorageRecord;
};

type ModifierFunction = (data: StorageRecord) => void;
type ErrorHandler = (error: Error) => void;

const modifyStorage = (
  key: string,
  modifier: ModifierFunction,
  onError?: ErrorHandler
): boolean => {
  try {
    const data = readObject(key);
    modifier(data);
    const success = safeSetItem(key, JSON.stringify(data));
    if (!success) {
      const error = new Error(`Failed to write to storage for key: ${key}`);
      if (onError) onError(error);
      return false;
    }
    return true;
  } catch (error) {
    if (onError) onError(error as Error);
    return false;
  }
};

const saveUserProperty = <T>(
  key: string,
  username: string,
  value: T,
  errorMessage: string,
  isWarn = false
): void => {
  modifyStorage(
    key,
    (data) => {
      data[username] = value;
    },
    (error) => {
      if (isWarn) {
        if (import.meta.env.DEV) console.warn(errorMessage, error);
      } else {
        console.error(errorMessage, error);
      }
    }
  );
};

const loadUserProperty = <T>(
  key: string,
  username: string,
  defaultValue: T,
  errorMessage: string
): T => {
  try {
    const data = readObject(key);
    if (Object.prototype.hasOwnProperty.call(data, username)) {
      const value = data[username];
      // For boolean properties, enforce type check to match original logic
      if (typeof defaultValue === 'boolean' && typeof value !== 'boolean') {
        return defaultValue;
      }
      if (value !== null && value !== undefined) {
        return value as T;
      }
    }
    return defaultValue;
  } catch (error) {
    console.error(errorMessage, error);
    return defaultValue;
  }
};

export interface GameProgress {
  score?: number;
  bestStreak?: number;
  roundsPlayed?: number;
  correctAnswers?: number;
  lastUpdated?: string;
}

export interface ScoreData {
  score: number;
  roundsPlayed: number;
  correctAnswers: number;
  bestStreak: number;
}

export interface HighScore extends ScoreData {
  timestamp: string;
}

export type DifficultyPreference = 'easy' | 'medium' | 'hard' | 'all';
export type GradeLevelPreference = string;

// FeedbackData interface matching the submitFeedback API signature
export interface FeedbackData {
  username: string;
  email?: string;
  type: string;
  message: string;
  url: string;
  userAgent?: string;
  diagnostics?: Record<string, unknown>;
}

interface FeedbackError {
  source: string;
  error: string;
}

interface StorageOperations {
  saveGameProgress: (username: string, progress: GameProgress) => void;
  loadGameProgress: (username: string) => GameProgress | null;
  saveMasteredWords: (username: string, words: string[]) => void;
  loadMasteredWords: (username: string) => string[];
  saveHighScore: (username: string, scoreData: ScoreData) => void;
  loadHighScores: (username: string) => HighScore[];
  saveDifficultyPreference: (username: string, difficulty: DifficultyPreference) => void;
  loadDifficultyPreference: (username: string) => DifficultyPreference | null;
  saveGradeLevelPreference: (username: string, gradeLevel: GradeLevelPreference) => void;
  loadGradeLevelPreference: (username: string) => GradeLevelPreference | null;
  saveWelcomePreference: (username: string, shouldShowWelcome: boolean) => void;
  loadWelcomePreference: (username: string) => boolean;
  saveSoundPreference: (username: string, enabled: boolean) => void;
  loadSoundPreference: (username: string) => boolean;
  saveAutoSubmitPreference: (username: string, enabled: boolean) => void;
  loadAutoSubmitPreference: (username: string) => boolean;
  saveUsername: (username: string | null) => void;
  loadUsername: () => string | null;
  saveFeedback: (feedbackData: FeedbackRequest) => Promise<boolean>;
  clearUserData: (username: string) => void;
}

export const storage: StorageOperations = {
  saveGameProgress: (username: string, progress: GameProgress): void => {
    modifyStorage(
      STORAGE_KEYS.GAME_PROGRESS,
      (allProgress) => {
        const existingProgress = (allProgress[username] as GameProgress) || {};
        allProgress[username] = {
          ...existingProgress,
          ...progress,
          lastUpdated: new Date().toISOString(),
        };
      },
      (error) => console.error('Failed to save game progress:', error)
    );
  },

  loadGameProgress: (username: string): GameProgress | null => {
    try {
      const allProgress = readObject(STORAGE_KEYS.GAME_PROGRESS);
      return (allProgress[username] as GameProgress) || null;
    } catch (error) {
      console.error('Failed to load game progress:', error);
      return null;
    }
  },

  saveMasteredWords: (username: string, words: string[]): void => {
    modifyStorage(
      STORAGE_KEYS.MASTERED_WORDS,
      (allMastered) => {
        allMastered[username] = words;
      },
      (error) => console.error('Failed to save mastered words:', error)
    );
  },

  loadMasteredWords: (username: string): string[] => {
    try {
      const allMastered = readObject(STORAGE_KEYS.MASTERED_WORDS);
      return (allMastered[username] as string[]) || [];
    } catch (error) {
      console.error('Failed to load mastered words:', error);
      return [];
    }
  },

  saveHighScore: (username: string, scoreData: ScoreData): void => {
    modifyStorage(
      STORAGE_KEYS.HIGH_SCORES,
      (allScores) => {
        if (!allScores[username]) {
          allScores[username] = [];
        }
        const userScores = allScores[username] as HighScore[];
        userScores.push({
          ...scoreData,
          timestamp: new Date().toISOString(),
        });
        userScores.sort((a, b) => b.score - a.score);
        allScores[username] = userScores.slice(0, 10);
      },
      (error) => console.error('Failed to save high score:', error)
    );
  },

  loadHighScores: (username: string): HighScore[] => {
    try {
      const allScores = readObject(STORAGE_KEYS.HIGH_SCORES);
      return (allScores[username] as HighScore[]) || [];
    } catch (error) {
      console.error('Failed to load high scores:', error);
      return [];
    }
  },

  saveDifficultyPreference: (username: string, difficulty: DifficultyPreference): void => {
    modifyStorage(
      STORAGE_KEYS.DIFFICULTY_PREFERENCE,
      (allPreferences) => {
        allPreferences[username] = difficulty;
      },
      (error) => {
        if (import.meta.env.DEV) console.warn('Failed to save difficulty preference:', error);
      }
    );
  },

  loadDifficultyPreference: (username: string): DifficultyPreference | null => {
    try {
      const allPreferences = readObject(STORAGE_KEYS.DIFFICULTY_PREFERENCE);
      return (allPreferences[username] as DifficultyPreference) || null;
    } catch (error) {
      console.error('Failed to load difficulty preference:', error);
      return null;
    }
  },

  saveGradeLevelPreference: (username: string, gradeLevel: GradeLevelPreference): void => {
    modifyStorage(
      STORAGE_KEYS.GRADE_LEVEL_PREFERENCE,
      (allPreferences) => {
        allPreferences[username] = gradeLevel;
      },
      (error) => console.error('Failed to save grade level preference:', error)
    );
  },

  loadGradeLevelPreference: (username: string): GradeLevelPreference | null => {
    try {
      const allPreferences = readObject(STORAGE_KEYS.GRADE_LEVEL_PREFERENCE);
      return (allPreferences[username] as GradeLevelPreference) || null;
    } catch (error) {
      console.error('Failed to load grade level preference:', error);
      return null;
    }
  },

  saveWelcomePreference: (username: string, shouldShowWelcome: boolean): void => {
    modifyStorage(
      STORAGE_KEYS.WELCOME_PREFERENCE,
      (allPreferences) => {
        allPreferences[username] = shouldShowWelcome;
      },
      (error) => console.error('Failed to save welcome preference:', error)
    );
  },

  loadWelcomePreference: (username: string): boolean => {
    try {
      const allPreferences = readObject(STORAGE_KEYS.WELCOME_PREFERENCE);
      if (Object.prototype.hasOwnProperty.call(allPreferences, username)) {
        return allPreferences[username] as boolean;
      }
      return true;
    } catch (error) {
      console.error('Failed to load welcome preference:', error);
      return true;
    }
  },

  saveSoundPreference: (username: string, enabled: boolean): void => {
    modifyStorage(
      STORAGE_KEYS.SOUND_PREFERENCE,
      (allPreferences) => {
        allPreferences[username] = Boolean(enabled);
      },
      (error) => console.error('Failed to save sound preference:', error)
    );
  },

  loadSoundPreference: (username: string): boolean => {
    try {
      const allPreferences = readObject(STORAGE_KEYS.SOUND_PREFERENCE);
      if (typeof allPreferences[username] === 'boolean') {
        return allPreferences[username] as boolean;
      }
      return true;
    } catch (error) {
      console.error('Failed to load sound preference:', error);
      return true;
    }
  },

  saveAutoSubmitPreference: (username: string, enabled: boolean): void => {
    modifyStorage(
      STORAGE_KEYS.AUTO_SUBMIT_PREFERENCE,
      (allPreferences) => {
        allPreferences[username] = Boolean(enabled);
      },
      (error) => console.error('Failed to save auto-submit preference:', error)
    );
  },

  loadAutoSubmitPreference: (username: string): boolean => {
    try {
      const allPreferences = readObject(STORAGE_KEYS.AUTO_SUBMIT_PREFERENCE);
      if (typeof allPreferences[username] === 'boolean') {
        return allPreferences[username] as boolean;
      }
      return true;
    } catch (error) {
      console.error('Failed to load auto-submit preference:', error);
      return true;
    }
  },

  saveUsername: (username: string | null): void => {
    try {
      const storageDriver = getStorageDriver();
      if (username) {
        safeSetItem(STORAGE_KEYS.USERNAME, username);
      } else {
        storageDriver.removeItem(STORAGE_KEYS.USERNAME);
      }
    } catch (error) {
      console.error('Failed to save username:', error);
    }
  },

  loadUsername: (): string | null => {
    try {
      return getStorageDriver().getItem(STORAGE_KEYS.USERNAME);
    } catch (error) {
      console.error('Failed to load username:', error);
      return null;
    }
  },

  saveFeedback: async (feedbackData: FeedbackRequest): Promise<boolean> => {
    const errors: FeedbackError[] = [];

    // Try API submission first
    try {
      await submitFeedback(feedbackData);
      return true;
    } catch (error) {
      errors.push({
        source: 'api',
        error: error instanceof Error ? error.message : String(error),
      });
      console.warn('Feedback API submission failed, falling back to local storage:', error);
    }

    // Fallback to local storage
    try {
      const existingFeedback = JSON.parse(
        getStorageDriver().getItem(STORAGE_KEYS.FEEDBACK) || '[]'
      ) as any[];
      existingFeedback.push(feedbackData);
      const saved = safeSetItem(STORAGE_KEYS.FEEDBACK, JSON.stringify(existingFeedback));

      if (saved) {
        return true;
      }

      errors.push({
        source: 'local',
        error: 'localStorage write failed',
      });
    } catch (error) {
      errors.push({
        source: 'local',
        error: error instanceof Error ? error.message : String(error),
      });
      console.error('Failed to save feedback:', error);
    }

    if (import.meta.env.DEV && errors.length > 0) {
      console.error('Feedback submission failed with errors:', errors);
    }
    return false;
  },

  clearUserData: (username: string): void => {
    const keysToClear = [
      STORAGE_KEYS.GAME_PROGRESS,
      STORAGE_KEYS.MASTERED_WORDS,
      STORAGE_KEYS.HIGH_SCORES,
      STORAGE_KEYS.DIFFICULTY_PREFERENCE,
      STORAGE_KEYS.GRADE_LEVEL_PREFERENCE,
      STORAGE_KEYS.WELCOME_PREFERENCE,
      STORAGE_KEYS.SOUND_PREFERENCE,
      STORAGE_KEYS.AUTO_SUBMIT_PREFERENCE,
    ];

    keysToClear.forEach((key) => {
      modifyStorage(
        key,
        (data) => {
          delete data[username];
        },
        (error) => console.error(`Failed to clear ${key} for user ${username}:`, error)
      );
    });
  },
};
